/*
  Girls Know drawer grid.
  Each drawer is a product. Clicking one opens the section's lightbox — which sits outside
  the slider, so the hero never changes size — and shows that product's image, animated by CSS.
  Escape or the close button closes it; while it is open the hero's autoplay is paused.
*/
if (!customElements.get('gk-drawer-box')) {
  customElements.define(
    'gk-drawer-box',
    class GkDrawerBox extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick);

        const dialog = this.dialog;
        if (dialog && !dialog.dataset.gkBound) {
          dialog.dataset.gkBound = 'true';
          dialog.addEventListener('click', this.onDialogClick);
        }
        // each box tracks its own trigger, so every box listens for the close
        if (dialog) dialog.addEventListener('close', this.onDialogClose);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.dialog?.removeEventListener('close', this.onDialogClose);
      }

      get dialog() {
        return document.getElementById(this.dataset.gkLightbox || '');
      }

      onClick = (event) => {
        const toggle = event.target.closest('[data-gk-drawer-toggle]');
        if (!toggle || !this.contains(toggle)) return;
        event.preventDefault();
        if (toggle.getAttribute('aria-expanded') === 'true') {
          this.closeAll();
        } else {
          this.open(toggle);
        }
      };

      onDialogClick = (event) => {
        // the close button, or the dark area around the image
        if (event.target.closest('[data-gk-lightbox-close]') || event.target === event.currentTarget) {
          event.currentTarget.close();
        }
      };

      onDialogClose = (event) => {
        const dialog = event.currentTarget;
        dialog.querySelectorAll('.gk-reveal').forEach((figure) => {
          figure.classList.remove('is-open');
          figure.hidden = true;
        });
        document.documentElement.classList.remove('gk-modal-open');

        const trigger = this.lastTrigger;
        this.lastTrigger = null;
        this.clearToggles();
        this.setSliderPause(false);
        if (trigger && document.contains(trigger)) trigger.focus();
      };

      open(button) {
        const dialog = this.dialog;
        const figure = document.getElementById(button.getAttribute('aria-controls'));
        if (!dialog || !figure) return;

        this.clearToggles();
        dialog.querySelectorAll('.gk-reveal').forEach((other) => {
          other.classList.remove('is-open');
          other.hidden = true;
        });

        button.setAttribute('aria-expanded', 'true');
        button.classList.add('is-open');
        this.lastTrigger = button;

        figure.hidden = false;
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', ''); // very old browsers still get a full-screen overlay
        }
        document.documentElement.classList.add('gk-modal-open');
        // let the browser paint the figure before the animation class lands on it
        requestAnimationFrame(() => figure.classList.add('is-open'));
        this.setSliderPause(true);
      }

      // also called by the hero slider when the slide changes
      closeAll() {
        const dialog = this.dialog;
        if (dialog && dialog.open) {
          dialog.close();
          return;
        }
        this.clearToggles();
        this.setSliderPause(false);
      }

      clearToggles() {
        this.querySelectorAll('[data-gk-drawer-toggle][aria-expanded="true"]').forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
          button.classList.remove('is-open');
        });
      }

      // an open lightbox shouldn't be undercut by the hero advancing behind it
      setSliderPause(paused) {
        const slider = this.closest('gk-hero-slider');
        if (!slider) return;
        if (paused) {
          slider.setAttribute('data-locked', '');
          if (typeof slider.stop === 'function') slider.stop();
          return;
        }
        const dialog = this.dialog;
        if (dialog && dialog.open) return; // still showing something
        slider.removeAttribute('data-locked');
        if (typeof slider.play === 'function') slider.play();
      }
    }
  );
}
